import api from "@/lib/api";

function fmtMoney(n, sym = "₹") {
  return `${sym}${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function openPrint(html) {
  const w = window.open("", "_blank", "width=380,height=640");
  if (!w) return;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 400);
}

export function printInvoice(order, settings) {
  const sym = settings?.currency_symbol || "₹";
  const rows = order.items.map((it) => `
    <tr>
      <td style="padding:4px 0">${it.qty}× ${it.name}</td>
      <td style="text-align:right">${fmtMoney(it.price * it.qty, sym)}</td>
    </tr>`).join("");
  const html = `<!doctype html><html><head><title>Invoice ${order.order_number}</title>
    <style>
      *{font-family:'Courier New',monospace;font-size:12px;color:#000}
      body{padding:16px;width:320px}
      h1{font-size:18px;text-align:center;margin:0}
      .muted{color:#555;font-size:11px}
      table{width:100%;border-collapse:collapse}
      .line{border-top:1px dashed #000;margin:8px 0}
      .tot{display:flex;justify-content:space-between}
      .big{font-size:15px;font-weight:bold}
      .center{text-align:center}
    </style></head><body>
    <h1>${settings?.restaurant_name || "ojent"}</h1>
    <div class="center muted">${settings?.address || ""}</div>
    <div class="center muted">${settings?.phone || ""}</div>
    <div class="center muted">GSTIN: ${settings?.gst_number || "-"}</div>
    <div class="line"></div>
    <div class="tot"><span>Invoice: ${order.order_number}</span><span>${order.order_type}</span></div>
    ${order.token_number ? `<div class="center big">TOKEN ${order.token_number}</div>` : ""}
    <div class="muted">${new Date(order.created_at).toLocaleString("en-IN")}</div>
    ${order.customer_name ? `<div class="muted">Customer: ${order.customer_name} ${order.customer_phone || ""}</div>` : ""}
    <div class="line"></div>
    <table>${rows}</table>
    <div class="line"></div>
    <div class="tot"><span>Subtotal</span><span>${fmtMoney(order.subtotal, sym)}</span></div>
    ${order.service_charge ? `<div class="tot"><span>Service charge</span><span>${fmtMoney(order.service_charge, sym)}</span></div>` : ""}
    <div class="tot"><span>CGST</span><span>${fmtMoney(order.cgst ?? (order.tax_total || 0) / 2, sym)}</span></div>
    <div class="tot"><span>SGST</span><span>${fmtMoney(order.sgst ?? (order.tax_total || 0) / 2, sym)}</span></div>
    ${order.discount ? `<div class="tot"><span>Discount${order.coupon_code ? ` (${order.coupon_code})` : ""}</span><span>-${fmtMoney(order.discount, sym)}</span></div>` : ""}
    ${order.redeem_amount ? `<div class="tot"><span>Loyalty</span><span>-${fmtMoney(order.redeem_amount, sym)}</span></div>` : ""}
    <div class="line"></div>
    <div class="tot big"><span>TOTAL</span><span>${fmtMoney(order.total, sym)}</span></div>
    <div class="muted">Paid via: ${(order.payments || []).map((p) => p.method).join(", ") || "unpaid"}</div>
    <div class="line"></div>
    <div class="center muted">Thank you! Visit again 🍽</div>
    </body></html>`;
  openPrint(html);
}

export function printKOT(order) {
  const rows = order.items.map((it) => `
    <tr><td style="padding:6px 0;font-size:15px;font-weight:bold">${it.qty}× ${it.name}</td></tr>
    ${it.notes ? `<tr><td class="muted">↳ ${it.notes}</td></tr>` : ""}
    ${it.packaging ? `<tr><td class="muted">📦 ${it.packaging}</td></tr>` : ""}`).join("");
  const packagingNotes = order.packaging_notes
    || (order.items || []).map((it) => it.packaging).filter(Boolean).join("; ");
  const html = `<!doctype html><html><head><title>KOT ${order.order_number}</title>
    <style>
      *{font-family:'Courier New',monospace;color:#000}
      body{padding:16px;width:320px}
      h1{font-size:16px;text-align:center;margin:0}
      .muted{color:#555;font-size:12px}
      .line{border-top:1px dashed #000;margin:8px 0}
      table{width:100%}
      .pkg{font-size:13px;font-weight:bold;margin-top:6px}
    </style></head><body>
    <h1>KITCHEN ORDER TICKET</h1>
    <div class="line"></div>
    <div style="font-size:14px"><b>${order.order_number}</b> · ${String(order.order_type || "").toUpperCase()}</div>
    <div class="muted">${new Date(order.created_at || Date.now()).toLocaleString("en-IN")}</div>
    ${order.brand_name ? `<div class="muted">Brand: ${order.brand_name}</div>` : ""}
    ${order.channel && order.channel !== "pos" ? `<div class="muted">Channel: ${String(order.channel).toUpperCase()}</div>` : ""}
    <div class="line"></div>
    <table>${rows}</table>
    <div class="line"></div>
    ${packagingNotes ? `<div class="pkg">Packaging: ${packagingNotes}</div>` : ""}
    ${order.notes ? `<div class="muted">Note: ${order.notes}</div>` : ""}
    </body></html>`;
  openPrint(html);
}

/** POST raw ESC/POS payload to a local print agent. */
export async function printViaAgent(job) {
  const agentUrl = job?.agent_url || job?.agentUrl || job?.agent_url;
  const raw = job?.raw_base64 || job?.rawBase64 || job?.raw_base64;
  if (!agentUrl) throw new Error("No agent_url on print job");
  const res = await fetch(agentUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      raw_base64: raw,
      text: job.text,
      kind: job.kind,
      station: job.station,
      printer_name: job.printer_name || job.printerName,
    }),
  });
  if (!res.ok) throw new Error(`Agent print failed (${res.status})`);
  return res.json().catch(() => ({ ok: true }));
}

/**
 * Request print jobs from the server, then print via browser or agent.
 * @param {string} orderId
 * @param {"kot"|"invoice"} kind
 * @param {{ station?: string, settings?: object }} [opts]
 */
export async function printOrderJobs(orderId, kind = "kot", opts = {}) {
  const body = { order_id: orderId, kind };
  if (opts.station) body.station = opts.station;
  const { data } = await api.post("/printers/jobs", body);
  const jobs = (data.jobs || []).map((j) => ({
    ...j,
    agent_url: j.agent_url || j.agentUrl || j.agent_url || "",
    raw_base64: j.raw_base64 || j.rawBase64 || j.raw_base64 || "",
    type: j.type || j.printer_type || "browser",
  }));
  const order = data.order || {};

  for (const job of jobs) {
    const jtype = job.type || job.printer_type;
    if ((job.agent_url || job.agentUrl) && (jtype === "agent" || jtype === "network")) {
      try {
        await printViaAgent(job);
      } catch {
        // Fall back to browser if agent unreachable
        if (kind === "invoice") printInvoice(order, opts.settings);
        else printKOT(order);
      }
    } else if (kind === "invoice") {
      printInvoice(order, opts.settings);
    } else {
      printKOT(order);
    }
  }
  if (!jobs.length) {
    if (kind === "invoice") printInvoice(order, opts.settings);
    else printKOT(order);
  }
  if (kind === "invoice" && orderId) {
    try { await api.post(`/orders/${orderId}/mark-printed`); } catch { /* ignore */ }
  }
  return data;
}
